import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import clsx from "clsx";
import { useLocale } from "@/i18n";
import { DURATION, EASE_OUT_EXPO, usePrefersReducedMotion } from "@/lib/motion";

const PHRASE_ROTATE_MS = 2800;
const PHRASE_FADE_MS = 220;

interface RotatingPhraseProps {
  phrase: string;
  compact: boolean;
  fullscreen: boolean;
}

function RotatingPhrase({ phrase, compact, fullscreen }: RotatingPhraseProps) {
  const [displayedPhrase, setDisplayedPhrase] = useState(phrase);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (phrase === displayedPhrase) return undefined;
    setVisible(false);
    const timer = window.setTimeout(() => {
      setDisplayedPhrase(phrase);
      setVisible(true);
    }, PHRASE_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [phrase, displayedPhrase]);

  return (
    <div
      className={clsx(
        "bk-avail-phrase-slot flex w-full items-center justify-center px-1 text-center",
        fullscreen ? "min-h-[3.5rem] text-lg" : compact ? "min-h-[2.5rem] text-sm" : "min-h-[3rem] text-base",
      )}
    >
      <p
        className={clsx(
          "bk-avail-phrase font-medium text-brand-900 transition-opacity duration-300 ease-in-out",
          visible ? "opacity-100" : "opacity-0",
        )}
      >
        {displayedPhrase}
      </p>
    </div>
  );
}

export interface AvailabilityCheckingScreenProps {
  variant?: "categories" | "alternatives";
  compact?: boolean;
  /** Auf Mobilgeräten als Vollbild-Overlay (Standard: ja, außer bei compact). */
  fullscreenOnMobile?: boolean;
  className?: string;
}

interface AvailabilityCheckingContentProps {
  variant: "categories" | "alternatives";
  compact: boolean;
  fullscreen: boolean;
  phrase: string;
  searchingPitches: string;
  searchingAlternatives: string;
}

function AvailabilityCheckingContent({
  variant,
  compact,
  fullscreen,
  phrase,
  searchingPitches,
  searchingAlternatives,
}: AvailabilityCheckingContentProps) {
  return (
    <>
      <div
        className={clsx(
          "relative overflow-hidden",
          fullscreen ? "mb-8 h-32" : compact ? "mb-3 h-16" : "mb-5 h-24",
        )}
      >
        <div className="bk-avail-scene__sky absolute inset-x-0 top-0 h-1/2 rounded-t-lg bg-gradient-to-b from-sky-100/80 to-transparent" />

        <span className="bk-avail-scene__cloud bk-avail-scene__cloud--1" aria-hidden="true">
          ☁️
        </span>
        <span className="bk-avail-scene__cloud bk-avail-scene__cloud--2" aria-hidden="true">
          ☁️
        </span>

        <span className="bk-avail-scene__tree bk-avail-scene__tree--1" aria-hidden="true">
          🌲
        </span>
        <span className="bk-avail-scene__tree bk-avail-scene__tree--2" aria-hidden="true">
          🌳
        </span>
        <span className="bk-avail-scene__tree bk-avail-scene__tree--3" aria-hidden="true">
          🌲
        </span>

        <div className="bk-avail-scene__road absolute inset-x-3 bottom-2 h-2 rounded-full bg-slate-300/80">
          <div className="bk-avail-scene__road-dash" aria-hidden="true" />
        </div>

        <span
          className={clsx("bk-avail-scene__van", fullscreen && "bk-avail-scene__van--fullscreen")}
          aria-hidden="true"
        >
          🚐
        </span>
      </div>

      <RotatingPhrase phrase={phrase} compact={compact} fullscreen={fullscreen} />

      {!compact && variant === "categories" ? (
        <p
          className={clsx(
            "mt-2 text-center text-brand-800/70",
            fullscreen ? "text-sm" : "text-xs",
          )}
        >
          {searchingPitches}
        </p>
      ) : null}

      {!compact && variant === "alternatives" ? (
        <p
          className={clsx(
            "mt-2 text-center text-brand-800/70",
            fullscreen ? "text-sm" : "text-xs",
          )}
        >
          {searchingAlternatives}
        </p>
      ) : null}
    </>
  );
}

export function AvailabilityCheckingScreen({
  variant = "categories",
  compact = false,
  fullscreenOnMobile = !compact,
  className,
}: AvailabilityCheckingScreenProps) {
  const { messages: m } = useLocale();
  const reducedMotion = usePrefersReducedMotion();
  const phrases =
    variant === "alternatives" ? m.availability.alternativePhrases : m.availability.checkingPhrases;
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    setPhraseIndex(0);
    const timer = window.setInterval(() => {
      setPhraseIndex((current) => (current + 1) % phrases.length);
    }, PHRASE_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [phrases.length, variant]);

  useEffect(() => {
    if (!fullscreenOnMobile) return undefined;

    const media = window.matchMedia("(max-width: 639px)");
    const lockScroll = () => {
      document.body.style.overflow = media.matches ? "hidden" : "";
    };
    lockScroll();
    media.addEventListener("change", lockScroll);
    return () => {
      document.body.style.overflow = "";
      media.removeEventListener("change", lockScroll);
    };
  }, [fullscreenOnMobile]);

  const phrase = phrases[phraseIndex];
  const statusProps = {
    role: "status" as const,
    "aria-live": "polite" as const,
    "aria-busy": true,
  };

  const inlineCard = (
    <div
      className={clsx(
        "overflow-hidden rounded-xl border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-emerald-50/80",
        compact ? "px-4 py-3" : "px-5 py-6",
        fullscreenOnMobile && "hidden sm:block",
        className,
      )}
      {...statusProps}
    >
      <AvailabilityCheckingContent
        variant={variant}
        compact={compact}
        fullscreen={false}
        phrase={phrase}
        searchingPitches={m.availability.searchingPitches}
        searchingAlternatives={m.availability.searchingAlternatives}
      />
    </div>
  );

  const mobileFullscreen = fullscreenOnMobile ? (
    <motion.div
      key="avail-fullscreen"
      className={clsx(
        "bk-avail-fullscreen fixed inset-0 z-[60] flex flex-col items-center justify-center bg-gradient-to-br from-brand-50 via-white to-emerald-50/90 px-6 sm:hidden",
        className,
      )}
      initial={reducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={reducedMotion ? undefined : { opacity: 0 }}
      transition={{ duration: DURATION.normal, ease: EASE_OUT_EXPO }}
      {...statusProps}
    >
      <div className="w-full max-w-sm">
        <AvailabilityCheckingContent
          variant={variant}
          compact={compact}
          fullscreen
          phrase={phrase}
          searchingPitches={m.availability.searchingPitches}
          searchingAlternatives={m.availability.searchingAlternatives}
        />
      </div>
    </motion.div>
  ) : null;

  if (!fullscreenOnMobile) {
    return inlineCard;
  }

  return (
    <>
      {typeof document !== "undefined"
        ? createPortal(mobileFullscreen, document.body)
        : mobileFullscreen}
      {inlineCard}
    </>
  );
}
